<template>
  <div class="main-body">
    <div class="title-bar">
      <h1>Contacts</h1>
      <div class="actions">
        <SearchMenu :options="searchResults" v-model="searchText" @updateContact="handleContactSelect" @forceOptionsUpdate="forceUpdate" placeholder="Search Contacts..." :noOutline="true" ref="searchMenu" />
        <div class="icon-button" @click="shareContact">
          <img id="noHover" src="../assets/share.svg" />
          <img id="hover" src="../assets/share-hover.svg" />
        </div>
        <div class="icon-button" @click="deleteContact" v-if="user.contactsAdmin">
          <img id="noHover" src="../assets/trash-bubble.svg" />
          <img id="hover" src="../assets/trash-bubble-hover.svg" />
        </div>
        <button class="square-btn" :class="{ saving: isSaving }" @click="saveContact()" :disabled="isSaving">Save <img src="../assets/spinner.svg" /></button>
        <button class="add-square-btn add-contact-btn" @click="openAddMenu" v-click-outside="closeAddMenu">
          <img src="../assets/plus-symbol.svg" />Add
          <div class="add-contact-menu z-index-10-fg dropdown" v-if="showAddMenu">
            <ul>
              <li @click="addCompany"><img src="../assets/company.svg" />Add Company</li>
              <li @click="addContact"><img src="../assets/individual.svg" />Add Contact</li>
            </ul>
          </div>
        </button>
      </div>
    </div>
    <div class="contact-container">
      <div v-if="!selectedContact" class="no-selection">
        <img src="../assets/company-splash.gif" alt="" />
        <h5>No Contact Selected</h5>
        <p>Search for a company or individual or click "Add" to create a new contact</p>
      </div>
      <Contact v-else ref="contact" :type="selectedContact.type" :entityid="selectedContact.value" @finishedSaving="isSaving = false" />
    </div>
  </div>
  <Confirmation
    v-if="showDeleteConfirm"
    @confirm="handleDeleteContact"
    @cancel="showDeleteConfirm = false"
    header="Delete Contact"
    :message="deleteConfirmMessage"
    confirmText="Delete"
    cancelText="Cancel"
    :serious="true" />
</template>

<script>
import SearchMenu from '../components/inputs/SearchMenu.vue';
import Contact from '../components/Contact.vue';
import Confirmation from '../components/Confirmation.vue';
import useSearchWorker from '../composables/useSearchWorker';
import useCompaniesQuery from '../composables/useCompaniesQuery';
import useContactsQuery from '../composables/useContactsQuery';
import useTempSettings from '../composables/useTempSettings';
import useCompanyMutate from '../composables/useCompanyMutate';
import useContactMutate from '../composables/useContactMutate';
import useAlerts from '../composables/useAlerts';
import useCurrentUser from '../composables/useCurrentUser';
import useSaveProtect from '../composables/useSaveProtect';

export default {
  name: 'Contacts',
  components: {
    SearchMenu,
    Contact,
    Confirmation,
  },
  setup() {
    useCompaniesQuery.useCompaniesQuery();
    useContactsQuery.useContactsQuery();
    const { state: saveProtectState } = useSaveProtect();
    const { user } = useCurrentUser();
    const { searchResults, searchText, forceUpdate } = useSearchWorker();
    const { setLastContact, getLastContact } = useTempSettings();
    const { deleteCompany } = useCompanyMutate();
    const { deleteContact } = useContactMutate();
    const { addAlert } = useAlerts();

    return {
      searchResults,
      searchText,
      forceUpdate,
      setLastContact,
      getLastContact,
      deleteCompany,
      deleteContact,
      addAlert,
      user,
      saveProtectState,
    };
  },
  data() {
    return {
      selectedContact: null,
      showFilter: false,
      isSaving: false,
      showAddMenu: false,
      showDeleteConfirm: false,
    };
  },
  mounted() {
    this.loadContactFromRoute();
  },
  watch: {
    $route() {
      this.loadContactFromRoute();
    },
  },
  computed: {
    deleteConfirmMessage() {
      if (!this.selectedContact) return '';
      const type = this.selectedContact.type === 'company' ? 'company' : 'contact';
      return `Are you sure you want to delete this ${type}? This action cannot be undone.`;
    },
  },
  methods: {
    loadContactFromRoute() {
      const { contactid, companyid } = this.$route.params;

      if (contactid) {
        this.selectedContact = {
          type: 'contact',
          value: contactid,
        };
        // Store in temp settings
        this.setLastContact('contact', contactid);
      } else if (companyid) {
        this.selectedContact = {
          type: 'company',
          value: companyid,
        };
        // Store in temp settings
        this.setLastContact('company', companyid);
      } else {
        // No route params, check temp settings for last contact
        const lastContact = this.getLastContact();
        if (lastContact && lastContact.entityid && lastContact.type) {
          this.selectedContact = {
            type: lastContact.type,
            value: lastContact.entityid,
          };

          // Update the URL to reflect the loaded contact
          const routeName = lastContact.type === 'contact' ? 'contacts' : 'companies';
          const paramName = lastContact.type === 'contact' ? 'contactid' : 'companyid';

          this.$router.replace({
            name: routeName,
            params: {
              [paramName]: lastContact.entityid,
            },
          });
        } else {
          this.selectedContact = null;
        }
      }

      this.$refs.searchMenu.handleFocus();
    },
    handleContactSelect(contact) {
      this.clearSearch();

      this.selectedContact = contact;

      // Store in temp settings
      this.setLastContact(contact.type, contact.value);

      // Update the URL based on the contact type
      const routeName = contact.type === 'contact' ? 'contacts' : 'companies';
      const paramName = contact.type === 'contact' ? 'contactid' : 'companyid';

      this.$router.push({
        name: routeName,
        params: {
          [paramName]: contact.value,
        },
      });
    },
    saveContact() {
      if (!this.selectedContact) return;
      this.isSaving = true;
      this.$refs.contact.saveContact();
    },
    openAddMenu() {
      this.clearSearch();
      this.showAddMenu ? null : (this.showAddMenu = true);
    },
    closeAddMenu() {
      this.showAddMenu = false;
    },
    clearSearch() {
      this.searchText = '';
      this.$nextTick(() => {
        this.$refs.searchMenu.handleFocus();
      });
    },
    addContact() {
      this.showAddMenu = false;
      this.clearSearch();
      this.$router.push({ name: 'contacts', params: { contactid: 'new' } });
    },
    addCompany() {
      this.showAddMenu = false;
      this.clearSearch();
      this.$router.push({ name: 'companies', params: { companyid: 'new' } });
    },
    deleteContact() {
      this.clearSearch();
      if (!this.selectedContact) {
        this.addAlert('No contact selected to delete', 'high', 3);
        return;
      }

      this.showDeleteConfirm = true;
    },
    async handleDeleteContact() {
      this.showDeleteConfirm = false;
      this.saveProtectState.isDirty = false;

      if (!this.selectedContact) return;

      if (this.selectedContact.value === 'new') {
        this.selectedContact = null;
        this.setLastContact(null, null);
        this.$router.push({ name: 'contacts' });
        return;
      }

      try {
        if (this.selectedContact.type === 'company') {
          await this.deleteCompany(this.selectedContact.value);
          this.addAlert('Company deleted successfully', 'low', 3);
        } else {
          await this.deleteContact(this.selectedContact.value);
          this.addAlert('Contact deleted successfully', 'low', 3);
        }

        // Clear the selected contact and navigate away
        this.selectedContact = null;
        this.setLastContact(null, null);

        // Navigate to the base contacts route without any ID
        this.$router.push({ name: 'contacts' });
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    },
    shareContact() {
      if (!this.selectedContact) return;
      this.$refs.contact.shareContact();
    },
  },
};
</script>

<style scoped lang="scss">
.main-body {
  flex: 1;
  height: calc(100% - $heightOfHeader);

  .title-bar {
    .actions {
      .search-menu {
        width: 25rem;
      }
    }
  }

  .contact-container {
    background: white;
    flex: 1;
    overflow: auto;

    .no-selection {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100%;

      h5 {
        font-size: $fontSize18;
        font-weight: 600;
        margin-bottom: 0.5rem;
        margin-top: 2rem;
        color: $textColor38;
      }

      p {
        color: $textColor38;
        font-size: $fontSize14;
        text-align: center;
        margin-top: 0;
        margin-bottom: 2rem;
      }
    }
  }

  .add-contact-btn {
    position: relative;

    .add-contact-menu {
      top: 3.4rem;
      right: 0rem;

      li {
        color: $textColor87;
        text-transform: none;
        font-size: $fontSize14;
        font-size: 1rem;
        font-weight: 400;
        padding: 0.5rem 0.8rem;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.3rem;

        img {
          height: 1.1rem;
        }

        &:hover {
          background-color: $hoverGrayColor;
        }
      }
    }
  }
}
</style>
